/**
 * Jack & Jones header: mobile menu drawer (native <dialog>) with its second level, and the cart
 * trigger's `aria-expanded`. Escape closing and focus return come from the dialog itself.
 */
const DESKTOP = window.matchMedia('(min-width: 750px)');
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @param {HTMLElement} header */
function setupHeader(header) {
  if (header.dataset.jjReady) return;
  header.dataset.jjReady = 'true';

  const dialog = /** @type {HTMLDialogElement | null} */ (header.querySelector('[data-jj-drawer]'));
  const opener = header.querySelector('[data-jj-drawer-open]');

  if (dialog && opener) {
    opener.addEventListener('click', () => {
      dialog.showModal();
      opener.setAttribute('aria-expanded', 'true');
      document.documentElement.setAttribute('scroll-lock', '');
    });

    // Closing slides the panel out to the left in 0.4 s, like the opening (Figma 73:1359 MOVE_OUT
    // 0.4 s EASE_OUT), then closes the dialog
    let closingDrawer = false;
    const closeDrawer = () => {
      if (!dialog.open || closingDrawer) return;
      if (REDUCED_MOTION.matches) return dialog.close();
      closingDrawer = true;
      dialog
        .animate({ transform: ['translateX(0)', 'translateX(-100%)'] }, { duration: 400, easing: 'ease-out', fill: 'forwards' })
        .finished.then(() => dialog.close(), () => {});
    };

    // Escape goes through the same slide-out
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDrawer();
    });

    dialog.addEventListener('close', () => {
      closingDrawer = false;
      dialog.getAnimations().forEach((animation) => animation.cancel());
      opener.setAttribute('aria-expanded', 'false');
      document.documentElement.removeAttribute('scroll-lock');
      closeSubmenus();
    });

    // Second level (e.g. SHOP): the arrow row opens it over the first level, the back button returns.
    const closeSubmenus = () => {
      header.querySelectorAll('[data-jj-drawer-sub-open][aria-expanded="true"]').forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
      });
      header.querySelectorAll('[data-jj-drawer-sub]').forEach((sub) => sub.setAttribute('hidden', ''));
    };

    header.querySelectorAll('[data-jj-drawer-sub-open]').forEach((button) => {
      const sub = document.getElementById(button.getAttribute('aria-controls') || '');
      if (!sub) return;

      button.addEventListener('click', () => {
        sub.getAnimations().forEach((animation) => animation.cancel());
        sub.removeAttribute('hidden');
        button.setAttribute('aria-expanded', 'true');
        sub.querySelector('[data-jj-drawer-sub-back]')?.focus();
      });

      // «Volver» slides the panel out to the left in 0.78 s (Figma SMART_ANIMATE ease-out), then hides it
      sub.querySelector('[data-jj-drawer-sub-back]')?.addEventListener('click', () => {
        button.setAttribute('aria-expanded', 'false');
        button.focus();
        const hide = () => sub.setAttribute('hidden', '');
        if (REDUCED_MOTION.matches) return hide();
        sub.inert = true;
        sub
          .animate({ transform: ['translateX(0)', 'translateX(-100%)'] }, { duration: 780, easing: 'ease-out' })
          .finished.then(() => {
            sub.inert = false;
            hide();
          }, () => (sub.inert = false));
      });
    });

    // Accordion rows of the second level («Fila desplegable» of SHOP: ROPA, CALZADO…) unfold and fold
    // in 0.4 s ease-out (Figma 1440:14817: SMART_ANIMATE 0.4 s EASE_OUT). Only one stays open: opening
    // a row folds the other open ones of the same list (QA h0982-header-34)
    /** @type {Map<HTMLDetailsElement, () => void>} */
    const folders = new Map();
    header.querySelectorAll('.jj-drawer__acc').forEach((element) => {
      const details = /** @type {HTMLDetailsElement} */ (element);
      const summary = details.querySelector('summary');
      const list = /** @type {HTMLElement | null} */ (details.querySelector('.jj-drawer__acc-list'));
      if (!summary || !list) return;
      let closing = false;

      const foldOthers = () => {
        const group = details.closest('.jj-drawer__list');
        folders.forEach((fold, other) => {
          if (other !== details && other.closest('.jj-drawer__list') === group) fold();
        });
      };

      /** @param {boolean} opening */
      const animateList = (opening) => {
        list.getAnimations().forEach((animation) => animation.cancel());
        closing = !opening;
        details.open = true;
        const { height, paddingTop, paddingBottom } = getComputedStyle(list);
        /** @param {string} value */
        const fromTo = (value) => (opening ? ['0px', value] : [value, '0px']);
        const animation = list.animate(
          {
            height: fromTo(height),
            paddingTop: fromTo(paddingTop),
            paddingBottom: fromTo(paddingBottom),
            overflow: ['hidden', 'hidden'],
          },
          { duration: 400, easing: 'ease-out' }
        );
        if (closing) {
          animation.addEventListener('finish', () => {
            details.open = false;
            closing = false;
          });
        }
      };

      folders.set(details, () => {
        if (!details.open || closing) return;
        if (REDUCED_MOTION.matches) details.open = false;
        else animateList(false);
      });

      summary.addEventListener('click', (event) => {
        if (REDUCED_MOTION.matches) return;
        event.preventDefault();
        // A click while it folds opens it again
        const opening = !details.open || closing;
        if (opening) foldOthers();
        animateList(opening);
      });

      // Opened some other way (find in page, a script setting [open], a click with reduced motion): it
      // unfolds the same way and the others fold
      details.addEventListener('toggle', () => {
        if (!details.open || closing) return;
        foldOthers();
        if (!REDUCED_MOTION.matches && !list.getAnimations().length) animateList(true);
      });
    });

    // Tab wraps inside the panel: past the last control the browser would move the focus out of the
    // modal (it landed on the skip link), so it goes back to the first one, and the other way round.
    dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const focusable = [
        ...dialog.querySelectorAll('a[href], button:not([disabled]), summary, input:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      ].filter((element) => !element.closest('[hidden], [inert]') && element.getClientRects().length > 0);
      const first = /** @type {HTMLElement | undefined} */ (focusable[0]);
      const last = /** @type {HTMLElement | undefined} */ (focusable[focusable.length - 1]);
      if (!first || !last) return;
      const active = document.activeElement;
      if (event.shiftKey ? active === first || !dialog.contains(active) : active === last) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    });

    // Click on the backdrop (the dialog box itself, outside the panel) closes it.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDrawer();
    });

    header.querySelectorAll('[data-jj-drawer-close], .jj-drawer a').forEach((element) => {
      element.addEventListener('click', closeDrawer);
    });

    DESKTOP.addEventListener('change', (event) => {
      if (event.matches && dialog.open) dialog.close();
    });
  }

  setupMegaMenus(header);
  setupDepartmentMenus(header);
  setupSearch(header);
  setupScrolledState(header);

  const cartTrigger = header.querySelector('[data-jj-cart-trigger]');
  const cartDrawer = document.getElementById('cart-drawer');
  if (cartTrigger && cartDrawer) {
    // <theme-drawer> drops `open` when the slide-out starts but only hands the focus back to the
    // trigger once the animation ends; meanwhile it would stay in the panel that is leaving (rule D4).
    // The panel is still a modal dialog then, so nothing outside can take it: it just leaves the panel.
    const sync = () => {
      const open = cartDrawer.hasAttribute('open');
      cartTrigger.setAttribute('aria-expanded', String(open));
      const active = document.activeElement;
      if (!open && active instanceof HTMLElement && cartDrawer.contains(active)) active.blur();
    };
    new MutationObserver(sync).observe(cartDrawer, { attributes: true, attributeFilter: ['open'] });
    sync();
  }
}

/**
 * Desktop mega menus: a click on the parent link opens its panel and turns the header solid;
 * a second click, Escape, the veil or a click outside the header close it.
 * @param {HTMLElement} header
 */
function setupMegaMenus(header) {
  const toggles = /** @type {HTMLElement[]} */ ([...header.querySelectorAll('[data-jj-mega-toggle]')]);
  if (!toggles.length) return;

  /** @param {HTMLElement} toggle */
  const panelOf = (toggle) => document.getElementById(toggle.getAttribute('aria-controls') || '');

  const closeAll = () => {
    for (const toggle of toggles) {
      toggle.setAttribute('aria-expanded', 'false');
      panelOf(toggle)?.setAttribute('hidden', '');
    }
    header.classList.remove('is-mega-open');
  };

  for (const toggle of toggles) {
    toggle.addEventListener('click', () => {
      const wasOpen = toggle.getAttribute('aria-expanded') === 'true';
      closeAll();
      if (wasOpen) return;
      panelOf(toggle)?.removeAttribute('hidden');
      toggle.setAttribute('aria-expanded', 'true');
      header.classList.add('is-mega-open');
    });
  }

  header.querySelectorAll('[data-jj-mega-close]').forEach((veil) => veil.addEventListener('click', closeAll));

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !header.classList.contains('is-mega-open')) return;
    const open = toggles.find((toggle) => toggle.getAttribute('aria-expanded') === 'true');
    closeAll();
    open?.focus();
  });

  document.addEventListener('click', (event) => {
    if (header.classList.contains('is-mega-open') && event.target instanceof Node && !header.contains(event.target)) {
      closeAll();
    }
  });

  DESKTOP.addEventListener('change', (event) => {
    if (!event.matches) closeAll();
  });
}

/**
 * Desktop: hovering a department (MAN, JUNIOR) opens its SHOP mega menu (snippets/jj-header-dept-mega.liquid);
 * the click still goes to the department's home. It closes when the pointer leaves the header or
 * goes down to the veil, with Escape, a click outside, or when the SHOP mega menu opens by click.
 * @param {HTMLElement} header
 */
function setupDepartmentMenus(header) {
  const links = /** @type {HTMLElement[]} */ ([...header.querySelectorAll('[data-jj-dept-mega]')]);
  if (!links.length) return;

  const canHover = window.matchMedia('(hover: hover)');
  /** @type {HTMLElement | null} */
  let current = null;
  let leaveTimer = 0;

  const close = () => {
    window.clearTimeout(leaveTimer);
    if (!current) return;
    current.setAttribute('hidden', '');
    current = null;
    if (!header.querySelector('[data-jj-mega-toggle][aria-expanded="true"]')) header.classList.remove('is-mega-open');
  };

  /** @param {HTMLElement} panel */
  const open = (panel) => {
    window.clearTimeout(leaveTimer);
    if (current === panel) return;
    // A SHOP menu opened by click gives way to the department's
    header.querySelectorAll('[data-jj-mega-toggle][aria-expanded="true"]').forEach((toggle) => {
      if (toggle instanceof HTMLElement) toggle.click();
    });
    current?.setAttribute('hidden', '');
    panel.removeAttribute('hidden');
    current = panel;
    header.classList.add('is-mega-open');
  };

  for (const link of links) {
    const panel = document.getElementById(link.dataset.jjDeptMega || '');
    if (!panel) continue;
    link.addEventListener('mouseenter', () => {
      if (DESKTOP.matches && canHover.matches) open(panel);
    });
  }

  // Leaving the header closes it after a short pause, so crossing the gap to the panel doesn't
  header.addEventListener('mouseleave', () => {
    window.clearTimeout(leaveTimer);
    leaveTimer = window.setTimeout(close, 150);
  });
  header.addEventListener('mouseenter', () => window.clearTimeout(leaveTimer));

  // The veil covers the page under the panel: reaching it means the pointer left the menu
  header.querySelectorAll('[data-jj-dept-close]').forEach((veil) => {
    veil.addEventListener('mouseenter', close);
    veil.addEventListener('click', close);
  });

  header.querySelectorAll('[data-jj-mega-toggle]').forEach((toggle) => {
    toggle.addEventListener('click', () => {
      if (!current) return;
      current.setAttribute('hidden', '');
      current = null;
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
  });

  document.addEventListener('click', (event) => {
    if (current && event.target instanceof Node && !current.contains(event.target) && !header.contains(event.target)) close();
  });

  DESKTOP.addEventListener('change', (event) => {
    if (!event.matches) close();
  });
}

/**
 * The search (Horizon's #search-modal) opens as a panel under this header: when it opens, the
 * header's bottom edge goes to `--jj-search-top` and the header turns solid.
 * @param {HTMLElement} header
 */
function setupSearch(header) {
  const dialog = document.querySelector('#search-modal dialog');
  if (!(dialog instanceof HTMLDialogElement)) return;

  const sync = () => {
    const open = dialog.hasAttribute('open');
    if (open) {
      dialog.style.setProperty('--jj-search-top', `${Math.max(0, header.getBoundingClientRect().bottom)}px`);
    }
    header.classList.toggle('is-search-open', open);
  };
  new MutationObserver(sync).observe(dialog, { attributes: true, attributeFilter: ['open'] });
}

/** @type {AbortController | undefined} */
let scrolledStateAbort;

/**
 * Home y JUNIOR: el header va transparente sobre la foto. El grupo del header queda fijo al scrollear
 * (section-jj-header.css) y el header pasa a Blanco apenas la página se mueve; arriba de todo vuelve a
 * transparente, sin transición (prototipo del Figma: «Home — Mobile 376» 750:26489 ⇄ «Home — Mobile 375»
 * 44:326 y JUNIOR 342:10910 ⇄ 1309:24397, NAVIGATE sin animación; Asana 1219831828584411).
 * Desde 990 el scroll vive en .page-wrapper (Horizon) y abajo de eso en la ventana: el scroll se escucha
 * en captura en el documento, que se entera de los dos.
 * @param {HTMLElement} header
 */
function setupScrolledState(header) {
  if (!header.classList.contains('jj-header--overlay') && !document.querySelector('.jj-hero--overlay-nav')) return;

  // Al volver a dibujar la sección (editor de temas) el header es otro: suelta los listeners del anterior
  scrolledStateAbort?.abort();
  scrolledStateAbort = new AbortController();
  const { signal } = scrolledStateAbort;

  const wrapper = document.querySelector('.page-wrapper');
  let frame = 0;
  const sync = () => {
    frame = 0;
    const top = Math.max(window.scrollY, wrapper instanceof HTMLElement ? wrapper.scrollTop : 0);
    header.classList.toggle('is-scrolled', top > 0);
  };
  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(sync);
  };

  document.addEventListener('scroll', schedule, { capture: true, passive: true, signal });
  window.addEventListener('resize', schedule, { passive: true, signal });
  signal.addEventListener('abort', () => cancelAnimationFrame(frame));
  sync();
}

function init() {
  document.querySelectorAll('[data-jj-header]').forEach((header) => setupHeader(/** @type {HTMLElement} */ (header)));
}

init();
document.addEventListener('shopify:section:load', init);
